import { ReactComponent as OfficeIcon } from 'assets/icons/icon-office.svg';
import Button from 'components/Button';
import Card from 'components/Card/Card';
import CardWithAside from 'components/Card/CardWithAside';
import {
  CardAside,
  CardBody,
  CardMain,
  CardTitle,
  CardWrapper,
} from 'components/Card/styled';
import CardFooter from 'components/CardFooter/CardFooter';
import DangerButton from 'components/DangerButton/DangerButton';
import ErrorMessage from 'components/ErrorMessage/ErrorMessage';
import MediumPill from 'components/MediumPill/MediumPill';
import NoEntitiesMessage from 'components/NoEntitiesMessage/NoEntitiesMessage';
import Page from 'components/Page';
import PageIcon from 'components/PageIcon/PageIcon';
import ResetButton from 'components/ResetButton/ResetButton';
import ScrollableBox from 'components/ScrollableBox/ScrollableBox';
import SearchInput from 'components/SearchInput';
import SubmitButton from 'components/SubmitButton/SubmitButton';
import React, { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { routeMap, useNavigate } from 'routeMap';
import { useTextInput, useWrappedUrqlMutation } from 'toolbox';
import Flex from 'toolbox/src/components/Flex/Flex';
import HeroIconExclamation from 'toolbox/src/components/HeroIcons/HeroIconExclamation';
import Stack from 'toolbox/src/components/Stack/Stack';
import { UpdateBusinessUnitForm } from './forms';
import { useGetBusinessUnitQuery } from './GetBusinessUnit.generated';
import { useRemoveBusinessUnitMutation } from './RemoveBusinessUnit.generated';
import { useUpdateBusinessUnitMutation } from './UpdateBusinessUnit.generated';
import Breadcrumbs from 'components/Breadcrumbs/Breadcrumbs';
import PreventLeavingFormIfTouched from 'toolbox/src/components/PreventLeavingFormIfTouched/PreventLeavingFormIfTouched';
import EditBusinessUnitExposureLimits from 'components/EditBusinessUnitExposureLimits/EditBusinessUnitExposureLimits';
import { BusinessUnitLiquidityLimits } from './BusinessUnitLiquidityLimits/BusinessUnitLiquidityLimits';
import { useUpsertBusinessUnitLimitsMutation } from 'components/EditBusinessUnitExposureLimits/UpsertBusinessUnitExposureLimits.generated';

interface ViewBusinessUnitProps {}

export const ViewBusinessUnit: React.FC<ViewBusinessUnitProps> = ({
  children,
}) => {
  const { id } = useParams<{ id: string }>();
  const searchInput = useTextInput();
  const [result] = useGetBusinessUnitQuery({
    variables: {
      id: Number(id),
      booksSearch: `%${searchInput.throttledValue}%`,
    },
  });
  const navigate = useNavigate();

  const business_unit = result?.data?.business_units_by_pk;
  const currency_books = business_unit?.currency_books || [];

  const banks = useMemo(() => {
    const banks = new Map<
      number,
      { id: number; name: string; image?: { public_url?: string } }
    >();

    if (currency_books?.length > 0) {
      const bank_accounts = [];

      currency_books?.forEach((currency_book) =>
        currency_book.bank_accounts?.forEach((bank_account) =>
          bank_accounts.push(bank_account),
        ),
      );

      /** Get unique banks */
      if (bank_accounts?.length > 0)
        bank_accounts?.forEach(({ bank }) => banks.set(bank.id, bank));
    }

    return (
      Array.from(banks.values())
        /** Sort banks by name */
        .sort((a, b) => a.name.localeCompare(b.name))
    );
  }, [currency_books]);

  const updateUnit = useWrappedUrqlMutation(useUpdateBusinessUnitMutation, {
    onSuccess: () => {
      toast.info('Updated Business Unit Successfully');
    },
  });

  const deleteUnit = useWrappedUrqlMutation(useRemoveBusinessUnitMutation, {
    onSuccess: (result) => {
      toast.info('Deleted Business Unit Successfully');
      navigate.viewOrgById({
        params: {
          id: result.delete_business_units.returning?.[0].organisation_id,
        },
      });
    },
    onError: () => {},
  });

  const upsertExposureLimits = useWrappedUrqlMutation(
    useUpsertBusinessUnitLimitsMutation,
  );

  if (!result.data) {
    return null;
  }

  return (
    <>
      <Page
        icon={
          <Flex mx={1} alignItems="center">
            <div>
              <PageIcon Icon={OfficeIcon} />
            </div>
            <Breadcrumbs
              links={[
                {
                  label: business_unit.organisation.name,
                  link: routeMap.viewOrgById({
                    params: {
                      id: business_unit.organisation.id,
                    },
                  }),
                },
                { label: business_unit.name },
              ]}
            />
          </Flex>
        }
      >
        <CardWrapper>
          <UpdateBusinessUnitForm.Wrapper
            onSubmit={updateUnit.dispatch}
            initialValues={{ id: business_unit.id, name: business_unit.name }}
            className="w-full"
          >
            <CardBody>
              <CardAside>
                <CardTitle>Manage Business Unit</CardTitle>
                <p className="text-sm">Make changes to this business unit.</p>
              </CardAside>
              <CardMain>
                <UpdateBusinessUnitForm.Inputs.name label="Name" />
              </CardMain>
            </CardBody>
            <CardFooter>
              <Flex justifyContent="flex-end" mx={3}>
                <ResetButton>Reset</ResetButton>
                <SubmitButton disableIfNoInputsTouched>Save</SubmitButton>
              </Flex>
            </CardFooter>
            <PreventLeavingFormIfTouched />
          </UpdateBusinessUnitForm.Wrapper>
        </CardWrapper>
        <CardWithAside
          title="Currency Books"
          footer={
            <Flex justifyContent="flex-end">
              <Button
                onClick={() =>
                  navigate.newCurrencyBook({
                    search: {
                      businessUnitId: id,
                    },
                  })
                }
              >
                Add New Currency Book
              </Button>
            </Flex>
          }
          aside={
            <Stack>
              <SearchInput placeholder="Filter" {...searchInput.inputProps} />
              <p className="text-sm">
                The currency books associated with this business unit.
              </p>
            </Stack>
          }
        >
          {!business_unit.currency_books.length && (
            <NoEntitiesMessage>
              There are currently no currency books in this business unit.
            </NoEntitiesMessage>
          )}
          <ScrollableBox>
            {business_unit.currency_books.map((currencyBook) => {
              return (
                <MediumPill
                  key={currencyBook.id}
                  Icon={OfficeIcon}
                  title={currencyBook.name}
                  to={routeMap.viewCurrencyBookById({
                    params: {
                      id: currencyBook.id,
                    },
                  })}
                >
                  <span className="mr-3 text-xs">
                    {currencyBook?.bank_accounts_aggregate?.aggregate?.count}{' '}
                    account(s)
                  </span>
                </MediumPill>
              );
            })}
          </ScrollableBox>
        </CardWithAside>
        {banks?.length > 0 && (
          <EditBusinessUnitExposureLimits
            banks={banks}
            exposureLimits={result?.data?.business_units_by_pk?.business_unit_bank_exposure_limits?.reduce(
              (obj, limit) => {
                return Object.assign(obj, {
                  [limit.bank_id]: {
                    exposure_limit: limit.exposure_limit,
                  },
                });
              },
              {},
            )}
            saveBankLimits={(bankLimits) => {
              return upsertExposureLimits.dispatch({
                businessUnitId: Number(id),
                objects: bankLimits.map((bank) => {
                  return {
                    bank_id: bank.id,
                    exposure_limit: bank.limit,
                    business_unit_id: Number(id),
                  };
                }),
              });
            }}
            onSuccess={() => toast('Updated exposure limits successfully')}
            onError={() => {
              toast.error('Something went wrong updating the exposure limits.');
            }}
          />
        )}
        {business_unit && (
          <BusinessUnitLiquidityLimits business_unit_id={Number(id)} />
        )}
        <Card title="Delete Business Unit">
          <p className="mb-4 text-sm text-gray-600">
            Deleting the business unit will also delete any currency books
            associated with it.
          </p>
          <Stack alignItems="flex-end">
            <DangerButton
              onClick={async () => {
                if (
                  // eslint-disable-next-line no-restricted-globals
                  confirm(
                    'Are you sure you want to delete this business unit?\n\nAll currency books, bank accounts and balances associated with this business unit will also be deleted.',
                  )
                )
                  deleteUnit.dispatch({ id: Number(id) });
              }}
            >
              <Flex mx={2}>
                <HeroIconExclamation />
                <p>Delete</p>
              </Flex>
            </DangerButton>
            <ErrorMessage {...deleteUnit} />
          </Stack>
        </Card>
      </Page>
    </>
  );
};

export default ViewBusinessUnit;
